import { Controller } from "@hotwired/stimulus";
import { computePosition, autoUpdate, flip, offset, shift } from "@floating-ui/dom";

/**
 * Dropdown controller using Floating UI for positioning.
 *
 * Mirrors the behaviour of Preline's HSDropdown:
 *   - `position: fixed` by default so the menu escapes overflow containers
 *   - `flip` middleware to auto-switch direction when clipped
 *   - `offset` middleware for the gap between trigger and menu
 *   - `shift` middleware to keep the menu within the viewport
 *   - `autoUpdate` to reposition on scroll / resize automatically
 *
 * Configuration via Stimulus values (data-attributes on the controller element):
 *   data-dropdown-placement-value  = Floating UI placement (default "bottom-start")
 *   data-dropdown-strategy-value   = "fixed" | "absolute"  (default "fixed")
 *   data-dropdown-offset-value     = gap in px              (default 4)
 */
export default class extends Controller {
    static targets = ["trigger", "menu"];

    static values = {
        placement: { type: String, default: "bottom-start" },
        strategy:  { type: String, default: "fixed" },
        offset:    { type: Number, default: 4 },
    };

    // ─── Lifecycle ────────────────────────────────────────────────
    connect() {
        this.isOpen = false;
        this._cleanup = null; // Floating UI autoUpdate disposer

        this._onOutsideClick = this._onOutsideClick.bind(this);
        this._onMenuClick    = this._onMenuClick.bind(this);

        document.addEventListener("click", this._onOutsideClick, true);

        if (this.hasMenuTarget) {
            this.menuTarget.addEventListener("click", this._onMenuClick);
        }
    }

    disconnect() {
        this._destroyFloating();
        document.removeEventListener("click", this._onOutsideClick, true);

        if (this.hasMenuTarget) {
            this.menuTarget.removeEventListener("click", this._onMenuClick);
        }
    }

    // ─── Public API (used from data-action) ───────────────────────
    toggle(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isOpen) {
            this.close();
        } else {
            this._closeOtherDropdowns();
            this.open();
        }
    }

    open() {
        if (this.isOpen || !this.hasMenuTarget || !this.hasTriggerTarget) return;

        // Set up Floating UI positioning (fixed + autoUpdate)
        this._setupFloating();

        // Show the menu
        this.menuTarget.classList.remove("hidden");
        this.element.classList.add("dropdown--open");

        // ARIA
        if (this.hasTriggerTarget) {
            this.triggerTarget.setAttribute("aria-expanded", "true");
        }

        this.isOpen = true;
    }

    close() {
        if (!this.isOpen) return;

        // Tear down Floating UI (stops autoUpdate listener)
        this._destroyFloating();

        // Hide the menu & reset inline styles
        if (this.hasMenuTarget) {
            this.menuTarget.classList.add("hidden");
            this.menuTarget.removeAttribute("style");
        }

        this.element.classList.remove("dropdown--open");

        // ARIA
        if (this.hasTriggerTarget) {
            this.triggerTarget.setAttribute("aria-expanded", "false");
        }

        this.isOpen = false;
    }

    // ─── Floating UI setup / teardown ─────────────────────────────
    _setupFloating() {
        const trigger = this.triggerTarget;
        const menu    = this.menuTarget;

        const middlewares = [
            offset(this.offsetValue),
            flip(),
            shift({ padding: 8 }),
        ];

        const updatePosition = () => {
            computePosition(trigger, menu, {
                placement:  this.placementValue,
                strategy:   this.strategyValue,
                middleware: middlewares,
            }).then(({ x, y, placement }) => {
                Object.assign(menu.style, {
                    position: this.strategyValue,
                    left:     `${x}px`,
                    top:      `${y}px`,
                    margin:   "0",
                });
                menu.dataset.placement = placement;
            });
        };

        // autoUpdate recalculates on scroll, resize, layout-shift
        // and returns a cleanup function.
        this._cleanup = autoUpdate(trigger, menu, updatePosition);
    }

    _destroyFloating() {
        if (this._cleanup) {
            this._cleanup();
            this._cleanup = null;
        }
    }

    // ─── Event handlers ───────────────────────────────────────────
    _onOutsideClick(event) {
        if (this.isOpen && !this.element.contains(event.target)) {
            this.close();
        }
    }

    _onMenuClick(event) {
        // When the user clicks a link / button / menuitem inside the menu, close.
        // This covers Turbo-powered links rendered asynchronously.
        const actionable = event.target.closest("a, button, [role='menuitem']");
        if (actionable && this.element.contains(actionable)) {
            this.close();
        }
    }

    // ─── Helpers ──────────────────────────────────────────────────
    _closeOtherDropdowns() {
        this.application
            .controllers
            .filter(c => c.identifier === "dropdown" && c !== this && c.isOpen)
            .forEach(c => c.close());
    }
}
